import styled from 'styled-components';

import { bidiProse } from 'components/bidi';

/**
 * A long answer, and the control that opens the rest of it.
 *
 * A table cell is one line tall everywhere in this panel, so a long free-text
 * answer ends in an ellipsis and stops. That is the right shape for a table;
 * what was missing was any way out of it. The control is the text itself —
 * quiet until hovered, so a column of answers still reads as a column of
 * answers rather than a column of buttons.
 */
export const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: var(--s2);
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: pointer;

  &:hover .answer-text {
    color: var(--primary);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: var(--r-sm);
  }

  svg {
    flex: none;
    width: 13px;
    height: 13px;
    stroke-width: 1.8;
    color: var(--text-faint);
  }

  &:hover svg {
    color: var(--primary);
  }
`;

/**
 * The text itself.
 *
 * It repeats the cell's one-line clip rather than inheriting it: AppTable's
 * rule reaches the cell and its immediate child, and inside this flex row the
 * text is neither. `min-width: 0` is what lets it shrink far enough to clip
 * instead of pushing the expand control out of the cell.
 */
export const Text = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The answer in full. Scrolls rather than growing the dialog off-screen. */
export const Answer = styled.div`
  ${bidiProse};
  max-height: 50vh;
  overflow-y: auto;
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-prose);
  white-space: pre-line;
  color: var(--text);
`;

/** Who wrote it — the row the cell came from, which the dialog leaves behind. */
export const Respondent = styled.div`
  margin-bottom: var(--s3);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-muted);
`;
